import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RefreshCw, ShieldCheck, AlertCircle, Copy, Search } from "lucide-react";
import WeKitLogo from "@/components/WeKitLogo";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface WebhookEvent {
  id: string;
  event_id: string | null;
  event_type: string | null;
  razorpay_order_id: string | null;
  razorpay_payment_id: string | null;
  signature_valid: boolean;
  processed: boolean;
  error: string | null;
  payload: any;
  created_at: string;
}

const PAGE_SIZE = 50;

const AdminWebhookLog = () => {
  const [events, setEvents] = useState<WebhookEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<"all" | "valid" | "invalid" | "failed">("all");
  const [selected, setSelected] = useState<WebhookEvent | null>(null);
  const [stats, setStats] = useState({ total: 0, valid: 0, invalid: 0, failed: 0 });

  const load = async () => {
    setLoading(true);
    let query = supabase
      .from("razorpay_webhook_events")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(PAGE_SIZE);

    if (filter === "valid") query = query.eq("signature_valid", true).eq("processed", true);
    if (filter === "invalid") query = query.eq("signature_valid", false);
    if (filter === "failed") query = query.eq("processed", false);

    if (q.trim()) {
      const term = q.trim();
      query = query.or(
        `event_type.ilike.%${term}%,event_id.ilike.%${term}%,razorpay_order_id.ilike.%${term}%,razorpay_payment_id.ilike.%${term}%,error.ilike.%${term}%`
      );
    }

    const { data, error } = await query;
    setLoading(false);
    if (error) {
      toast({ title: "Failed to load", description: error.message, variant: "destructive" });
      return;
    }
    setEvents((data ?? []) as WebhookEvent[]);

    // 24h stats
    const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    const { data: recent } = await supabase
      .from("razorpay_webhook_events")
      .select("signature_valid, processed")
      .gte("created_at", since);
    const all = recent ?? [];
    setStats({
      total: all.length,
      valid: all.filter((e) => e.signature_valid && e.processed).length,
      invalid: all.filter((e) => !e.signature_valid).length,
      failed: all.filter((e) => e.signature_valid && !e.processed).length,
    });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const statusBadge = (e: WebhookEvent) => {
    if (!e.signature_valid) return <Badge variant="destructive">invalid signature</Badge>;
    if (!e.processed) return <Badge variant="destructive">failed</Badge>;
    return <Badge className="bg-green-600 hover:bg-green-600">processed</Badge>;
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 border-b border-border/50 bg-background/95 backdrop-blur">
        <div className="flex items-center justify-between px-4 py-3 max-w-7xl mx-auto">
          <WeKitLogo size="sm" />
          <Badge variant="outline">Admin · Webhook Event Log</Badge>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="border-border/50"><CardContent className="pt-6">
            <p className="text-xs text-muted-foreground">Events (24h)</p>
            <p className="text-2xl font-bold">{stats.total}</p>
          </CardContent></Card>
          <Card className="border-border/50"><CardContent className="pt-6">
            <p className="text-xs text-muted-foreground">Processed OK</p>
            <p className="text-2xl font-bold text-green-600">{stats.valid}</p>
          </CardContent></Card>
          <Card className="border-border/50"><CardContent className="pt-6">
            <p className="text-xs text-muted-foreground">Invalid signature</p>
            <p className="text-2xl font-bold text-destructive">{stats.invalid}</p>
          </CardContent></Card>
          <Card className="border-border/50"><CardContent className="pt-6">
            <p className="text-xs text-muted-foreground">Failed to process</p>
            <p className="text-2xl font-bold text-destructive">{stats.failed}</p>
          </CardContent></Card>
        </div>

        {/* Toolbar */}
        <Card className="border-border/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Incoming Razorpay payloads</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="event type, event id, order/payment id, error…"
                  className="pl-9"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              </div>
              <Select value={filter} onValueChange={(v) => setFilter(v as any)}>
                <SelectTrigger className="w-full sm:w-[180px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All events</SelectItem>
                  <SelectItem value="valid">Processed OK</SelectItem>
                  <SelectItem value="invalid">Invalid signature</SelectItem>
                  <SelectItem value="failed">Failed to process</SelectItem>
                </SelectContent>
              </Select>
              <Button type="submit" disabled={loading}>
                {loading ? "Loading…" : "Search"}
              </Button>
              <Button type="button" variant="ghost" size="icon" onClick={load} title="Refresh">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="border-border/50">
          <CardContent className="pt-6">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Time</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>Signature</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Order / Payment</TableHead>
                    <TableHead>Error</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.map((e) => (
                    <TableRow
                      key={e.id}
                      className="cursor-pointer hover:bg-muted/40"
                      onClick={() => setSelected(e)}
                    >
                      <TableCell className="text-xs whitespace-nowrap">
                        {new Date(e.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{e.event_type ?? "—"}</TableCell>
                      <TableCell>
                        {e.signature_valid ? (
                          <span className="inline-flex items-center gap-1 text-green-600 text-xs">
                            <ShieldCheck className="h-3.5 w-3.5" /> valid
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-destructive text-xs">
                            <AlertCircle className="h-3.5 w-3.5" /> invalid
                          </span>
                        )}
                      </TableCell>
                      <TableCell>{statusBadge(e)}</TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {e.razorpay_order_id || e.razorpay_payment_id || "—"}
                      </TableCell>
                      <TableCell className="text-xs text-destructive max-w-[240px] truncate">
                        {e.error ?? ""}
                      </TableCell>
                    </TableRow>
                  ))}
                  {events.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground py-10">
                        No webhook events match the filter.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Showing latest {events.length} of last {PAGE_SIZE}. Use search to narrow.
            </p>
          </CardContent>
        </Card>
      </main>

      {/* Detail */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-mono text-sm">
              {selected?.event_type ?? "webhook event"}
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><span className="text-muted-foreground">Received:</span> {new Date(selected.created_at).toLocaleString()}</div>
                <div><span className="text-muted-foreground">Signature:</span> {selected.signature_valid ? <span className="text-green-600">valid</span> : <span className="text-destructive">invalid</span>}</div>
                <div><span className="text-muted-foreground">Processed:</span> {selected.processed ? "yes" : "no"}</div>
                <div className="font-mono text-xs"><span className="text-muted-foreground font-sans">Event ID:</span> {selected.event_id ?? "—"}</div>
                <div className="font-mono text-xs"><span className="text-muted-foreground font-sans">Order:</span> {selected.razorpay_order_id ?? "—"}</div>
                <div className="font-mono text-xs"><span className="text-muted-foreground font-sans">Payment:</span> {selected.razorpay_payment_id ?? "—"}</div>
              </div>
              {selected.error && (
                <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
                  <p className="font-medium mb-1">Error</p>
                  <p className="font-mono whitespace-pre-wrap">{selected.error}</p>
                </div>
              )}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="font-medium">Raw payload</p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(JSON.stringify(selected.payload, null, 2));
                      toast({ title: "Copied", description: "Payload copied to clipboard" });
                    }}
                  >
                    <Copy className="h-3.5 w-3.5 mr-1" /> Copy
                  </Button>
                </div>
                <pre className="rounded-md border border-border/50 bg-muted/30 p-3 text-xs font-mono overflow-x-auto max-h-[50vh]">
                  {JSON.stringify(selected.payload, null, 2)}
                </pre>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminWebhookLog;
